import type { StateCreator } from 'zustand'
import type { LogEntry, ToolCall } from '../../types'

// Height persistence constants
const TELEMETRY_HEIGHT_STORAGE_KEY = 'telemetry-pane-height'
const DEFAULT_TELEMETRY_HEIGHT = 256
const MIN_TELEMETRY_HEIGHT = 120
const MAX_TELEMETRY_HEIGHT = 600

function loadTelemetryHeight(): number {
  try {
    const saved = localStorage.getItem(TELEMETRY_HEIGHT_STORAGE_KEY)
    if (saved) {
      const parsed = Number(saved)
      if (!isNaN(parsed) && parsed >= MIN_TELEMETRY_HEIGHT && parsed <= MAX_TELEMETRY_HEIGHT) {
        return parsed
      }
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_TELEMETRY_HEIGHT
}

function saveTelemetryHeight(height: number): void {
  try {
    localStorage.setItem(TELEMETRY_HEIGHT_STORAGE_KEY, String(height))
  } catch {
    // localStorage not available
  }
}

export interface TelemetrySlice {
  isTelemetryOpen: boolean
  telemetryHeight: number
  logs: LogEntry[]
  toolCalls: ToolCall[]
  toggleTelemetry: () => void
  setTelemetry: (open: boolean) => void
  setTelemetryHeight: (height: number) => void
  addLog: (log: LogEntry) => void
  addToolCall: (toolCall: ToolCall) => void
  updateToolCall: (tool_use_id: string, patch: Partial<ToolCall>) => void
  updateToolCallByWorkflow: (workflowId: number, patch: Partial<ToolCall>) => void
  clearLogs: () => void
  clearToolCalls: () => void
}

export const createTelemetrySlice: StateCreator<
  TelemetrySlice,
  [],
  [],
  TelemetrySlice
> = (set) => ({
  isTelemetryOpen: true,
  telemetryHeight: loadTelemetryHeight(),
  logs: [],
  toolCalls: [],
  toggleTelemetry: () => set((state) => ({ isTelemetryOpen: !state.isTelemetryOpen })),
  setTelemetry: (open: boolean) => set({ isTelemetryOpen: open }),
  setTelemetryHeight: (height: number) => {
    const clamped = Math.max(MIN_TELEMETRY_HEIGHT, Math.min(MAX_TELEMETRY_HEIGHT, height))
    saveTelemetryHeight(clamped)
    set({ telemetryHeight: clamped })
  },
  addLog: (log) => set((state) => ({ logs: [...state.logs, log].slice(-1000) })),
  addToolCall: (toolCall) =>
    set((state) => ({ toolCalls: [...state.toolCalls, toolCall].slice(-500) })),
  updateToolCall: (tool_use_id, patch) =>
    set((state) => ({
      toolCalls: state.toolCalls.map((tc) =>
        tc.tool_use_id === tool_use_id ? { ...tc, ...patch } : tc
      ),
    })),
  updateToolCallByWorkflow: (workflowId, patch) =>
    set((state) => {
      // Find the most recent RUNNING tool call for this workflow
      const runningCalls = state.toolCalls
        .map((tc, idx) => ({ tc, idx }))
        .filter(({ tc }) => tc.workflowId === workflowId && tc.status === 'RUNNING')
        .sort((a, b) => new Date(b.tc.timestamp).getTime() - new Date(a.tc.timestamp).getTime())

      if (runningCalls.length === 0) {
        return { toolCalls: state.toolCalls }
      }

      const { idx } = runningCalls[0]
      const updated = [...state.toolCalls]
      updated[idx] = { ...updated[idx], ...patch }
      return { toolCalls: updated }
    }),
  clearLogs: () => set({ logs: [] }),
  clearToolCalls: () => set({ toolCalls: [] }),
})

// Export constants for use in components
export { MIN_TELEMETRY_HEIGHT, MAX_TELEMETRY_HEIGHT, DEFAULT_TELEMETRY_HEIGHT }

